import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidDateTimePart } from './versionName';
import { FormFieldAny } from '@/components/FormField';

interface VersionNameFieldProps {
  numberPart: string;
  dateTimePart: string;
  onDateTimeChange: (dateTimePart: string) => void;
  // Only provided in the create view, where the file-derived number may be overridden.
  onNumberChange?: (numberPart: string) => void;
  // Enables the double-click override of the otherwise locked number part.
  allowForceNumberEdit?: boolean;
  status?: 'idle' | 'checking' | 'exists' | 'available';
  // When false, the availability/checking feedback is rendered by the parent instead of inline.
  showAvailabilityStatus?: boolean;
}

// Controlled input for a firmware version: a locked (file-derived) number part next to the
// editable date/time part. Debouncing and the availability check live in the version editor
// hook, so this component only renders the current values and reports edits back to the parent.
export function VersionNameField({
  numberPart,
  dateTimePart,
  onDateTimeChange,
  onNumberChange,
  allowForceNumberEdit = false,
  status = 'idle',
  showAvailabilityStatus = true,
}: VersionNameFieldProps) {
  const { t } = useTranslation();
  const [isNumberUnlocked, setIsNumberUnlocked] = useState(false);

  // A non-empty date/time that does not match the expected format is shown as an error.
  const hasFormatError = dateTimePart !== '' && !isValidDateTimePart(dateTimePart);

  // Uppercases each keystroke and reports the edited value; the editor hook resets the check
  // status and re-runs the availability check once typing settles.
  const handleDateTimeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onDateTimeChange(event.target.value.toUpperCase());
    },
    [onDateTimeChange]
  );

  // Unlock the number field only when the create view allows the override.
  const handleNumberDoubleClick = useCallback(() => {
    if (allowForceNumberEdit) setIsNumberUnlocked(true);
  }, [allowForceNumberEdit]);

  const dateTimeBorderClass = hasFormatError || status === 'exists'
    ? 'border-error focus:ring-error focus:border-error'
    : status === 'available'
      ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
      : 'border-border focus:ring-primary focus:border-primary';

  return (
    <FormFieldAny
      label={'firmware.sidePanel.versionName'}
      labelKey={'firmware.sidePanel.versionName'}
    >
      <div className="flex items-stretch gap-2">
        {renderNumberPart()}
        <input
          type="text"
          value={dateTimePart}
          onChange={handleDateTimeChange}
          placeholder={t('firmware.sidePanel.versionDateTimePlaceholder')}
          className={`flex-1 py-2 px-3 text-sm rounded-lg border bg-surface text-onsurface placeholder:text-muted-foreground focus:outline-none focus:ring-1 ${dateTimeBorderClass}`}
        />
      </div>
      {renderStatusMessage()}

    </FormFieldAny>
  );

  // Renders the file-derived number either as a locked segment or, once unlocked, as an input.
  function renderNumberPart() {
    if (isNumberUnlocked && onNumberChange) {
      return (
        <input
          type="text"
          value={numberPart}
          onChange={(event) => onNumberChange(event.target.value.replace(/\D/g, ''))}
          placeholder={t('firmware.sidePanel.versionNumberPlaceholder')}
          className="w-20 py-2 px-3 text-sm rounded-lg border border-primary bg-surface text-onsurface focus:outline-none focus:ring-1 focus:ring-primary"
        />
      );
    }

    return (
      <button
        type="button"
        onDoubleClick={handleNumberDoubleClick}
        title={allowForceNumberEdit ? t('firmware.sidePanel.versionNumberForceHint') : undefined}
        className="w-20 py-2 px-3 text-sm rounded-lg border border-border bg-surface-darker text-muted-foreground text-left truncate cursor-default"
      >
        {numberPart || t('firmware.sidePanel.versionNumberFromFile')}
      </button>
    );
  }

  // Shows the inline format, availability and checking feedback below the field.
  // The availability part can be suppressed so the parent can render it elsewhere.
  function renderStatusMessage() {
    if (hasFormatError) {
      return <p className="mt-1 text-xs text-error">{t('firmware.sidePanel.versionFormatError')}</p>;
    }
    if (!showAvailabilityStatus) {
      return null;
    }
    if (status === 'exists') {
      return <p className="mt-1 text-xs text-error">{t('firmware.sidePanel.versionExists')}</p>;
    }
    if (status === 'available') {
      return <p className="mt-1 text-xs text-green-600">{t('firmware.sidePanel.versionAvailable')}</p>;
    }
    if (status === 'checking') {
      return (
        <div className="mt-1 flex items-center gap-2">
          <div className="w-3 h-3 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-xs text-yellow-500">{t('firmware.sidePanel.checking')}</p>
        </div>
      );
    }
    return null;
  }
}
